import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store';
import { hasCallbackTask, outageProduct, type GuideChatProduct, type UnavailableResponse } from './types';
import { conversationTokenFor } from './session';

export const APPOINTMENT_PROXY = '/api/poc-guide-chat/appointment';
export const TRANSCRIPT_QUERY_PARAMETER = 'conversation';
export const UNAVAILABLE_RULE_ID = 'assistant_indisponible';

// Mirrors the backend labels for when the backend could not send them, since it may be the part that is down.
export const APPOINTMENT_TEXTS = {
  callNow: 'Être rappelé maintenant',
  pickSlot: 'Choisir un créneau',
  confirmSlot: 'Confirmer le rendez-vous',
  leaveContact: 'Être rappelé par un conseiller',
  callbackSubmit: 'Être rappelé par un expert',
  sending: 'Enregistrement…',
  callbackConfirmed:
    "C'est noté : un conseiller Réassurez-moi vous rappelle dès que possible. Vous recevrez une confirmation par e-mail et par SMS.",
  appointmentConfirmed:
    "C'est noté : un conseiller Réassurez-moi vous rappelle au créneau choisi. Vous recevrez une confirmation par e-mail et par SMS.",
  missingContact: 'Prénom, nom, téléphone et e-mail sont requis.',
  missingSlot: 'Choisissez un créneau.',
  callbackFailed: "Votre demande de rappel n'a pas pu être enregistrée.",
  firstName: 'Prénom',
  lastName: 'Nom',
  phone: 'Téléphone',
  email: 'E-mail',
} as const;

export type BookingMode = 'callNow' | 'slot' | 'callback';

export interface ContactFields {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export const EMPTY_CONTACT: ContactFields = { firstName: '', lastName: '', phone: '', email: '' };

export interface BookingConfirmationTexts {
  slot?: string;
  callback?: string;
}

export interface AppointmentBookingBody {
  slotId: string | null;
  callNow: boolean;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  product: string;
  handover: Record<string, unknown>;
  quoteToken?: string;
  conversationToken?: string;
}

export interface UnavailableCallback {
  kind: 'callback';
  product: string;
  submitLabel: string;
  confirmation: string;
  handover: Record<string, unknown>;
}

// The 503 body carries more than the shared type declares; api.ts spreads it whole, so these fields survive.
export interface UnavailableWithCallback extends UnavailableResponse {
  conversationId?: string;
  callback?: Partial<UnavailableCallback> | null;
}

export type BookingResult = { ok: true; confirmation: string | null; booking: unknown } | { ok: false; error: string };

// The immediate callback is the default answer to an explicit request while the floor is open.
export function initialMode(card: { callNow?: boolean }): 'callNow' | 'slot' {
  return card.callNow ? 'callNow' : 'slot';
}

export function effectiveMode(selected: 'callNow' | 'slot', canBookWithoutSlot: boolean): BookingMode {
  if (selected === 'callNow') return 'callNow';
  return canBookWithoutSlot ? 'callback' : 'slot';
}

export function submitLabel(mode: BookingMode, callNowLabel?: string | null): string {
  if (mode === 'callNow') return callNowLabel || APPOINTMENT_TEXTS.callNow;
  return mode === 'callback' ? APPOINTMENT_TEXTS.leaveContact : APPOINTMENT_TEXTS.confirmSlot;
}

export function confirmationText(mode: BookingMode, fromServer: string | null, card?: BookingConfirmationTexts): string {
  if (fromServer) return fromServer;
  return mode === 'slot'
    ? (card?.slot ?? APPOINTMENT_TEXTS.appointmentConfirmed)
    : (card?.callback ?? APPOINTMENT_TEXTS.callbackConfirmed);
}

export function trimContact(contact: ContactFields): ContactFields {
  return {
    firstName: contact.firstName.trim(),
    lastName: contact.lastName.trim(),
    phone: contact.phone.trim(),
    email: contact.email.trim(),
  };
}

export function missingContactFields(contact: ContactFields): (keyof ContactFields)[] {
  const trimmed = trimContact(contact);
  return (Object.keys(trimmed) as (keyof ContactFields)[]).filter((field) => trimmed[field] === '');
}

const QUOTE_TOKEN = /^[A-Za-z0-9_-]{6,255}$/;

type QuoteTokenSource = { getState: () => { quote?: { token?: unknown } | null } };

// A cotation already started in this browser: the adviser's task goes on that quote rather than on a new one.
export function knownQuoteToken(store: QuoteTokenSource = useLoanComparatorStore): string | null {
  try {
    const token = store.getState().quote?.token;
    return typeof token === 'string' && QUOTE_TOKEN.test(token) ? token : null;
  } catch {
    return null;
  }
}

export function bookingBody(input: {
  mode: BookingMode;
  slotId?: string | null;
  contact: ContactFields;
  product: string;
  handover: Record<string, unknown>;
  quoteToken?: string | null;
}): AppointmentBookingBody {
  const body: AppointmentBookingBody = {
    slotId: input.mode === 'slot' ? (input.slotId ?? null) : null,
    callNow: input.mode === 'callNow',
    ...trimContact(input.contact),
    product: input.product,
    handover: input.handover,
  };
  if (input.quoteToken) body.quoteToken = input.quoteToken;
  return body;
}

export async function postBooking(body: AppointmentBookingBody, fetchImpl: typeof fetch = fetch): Promise<BookingResult> {
  try {
    const conversationToken = body.conversationToken ?? conversationTokenFor(body.handover.conversationId);
    const res = await fetchImpl(APPOINTMENT_PROXY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(conversationToken ? { ...body, conversationToken } : body),
    });
    const data = (await res.json().catch(() => null)) as {
      error?: string;
      failedStep?: string;
      confirmation?: string;
      booking?: unknown;
    } | null;
    if (!res.ok || !data || data.error) {
      const error = data?.error || `HTTP ${res.status}`;
      return { ok: false, error: data?.failedStep ? `${error} (étape « ${data.failedStep} »)` : error };
    }
    return { ok: true, confirmation: typeof data.confirmation === 'string' ? data.confirmation : null, booking: data.booking ?? null };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

// A dead backend sends no form, so a minimal hand-over is rebuilt from what the widget still knows.
export function unavailableCallbackOf(
  info: UnavailableWithCallback,
  known: { conversationId?: string | null; question?: string | null; product?: GuideChatProduct | null } = {},
): UnavailableCallback | null {
  if (info.appointment === false || !hasCallbackTask(outageProduct(info, known.product))) return null;
  const sent = info.callback ?? {};
  return {
    kind: 'callback',
    product: sent.product ?? 'loan',
    submitLabel: sent.submitLabel ?? APPOINTMENT_TEXTS.callbackSubmit,
    confirmation: sent.confirmation ?? APPOINTMENT_TEXTS.callbackConfirmed,
    handover: sent.handover ?? {
      conversationId: known.conversationId ?? info.conversationId ?? '',
      question: known.question ?? '',
      outcome: 'appointment',
      ruleId: UNAVAILABLE_RULE_ID,
    },
  };
}

const CONVERSATION_ID = /^[A-Za-z0-9_-]{8,64}$/;

// The adviser's CRM note links to the admin chat page with the conversation in the query string.
export function conversationFromQuery(search: string | null | undefined): string | null {
  const value = new URLSearchParams(search ?? '').get(TRANSCRIPT_QUERY_PARAMETER)?.trim() ?? '';
  return CONVERSATION_ID.test(value) ? value : null;
}
